import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { getTheme } from '../theme';
import MainLayout from '../components/Layout/MainLayout';
import Card from '../components/common/Card';
import { api } from '../services/api';
import { People, AssignmentInd, Receipt, Notifications, Storage, AttachMoney, TrendingUp } from '@mui/icons-material';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const StatCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  font-size: 28px;
  ${props => props.theme?.isDark && `
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.2);
  `}
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  `}
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  `}
`;

const Dashboard = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [stats, setStats] = useState({
    customers: 0,
    users: 0,
    subscriptions: 0,
    notifications: 0,
  });
  const [promptStats, setPromptStats] = useState({
    todayTokens: 0,
    todayCost: 0,
    monthlyCost: 0,
  });
  const [loading, setLoading] = useState(true);
  const [promptLoading, setPromptLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [customers, users, subscriptions, notifications] = await Promise.all([
          api.getCustomers({ limit: 1 }),
          api.getUsers({ limit: 1 }),
          api.getSubscriptions({ limit: 1 }),
          api.getNotifications({ limit: 1 }),
        ]);

        setStats({
          customers: customers.totalResults || 0,
          users: users.totalResults || 0,
          subscriptions: subscriptions.totalResults || 0,
          notifications: notifications.totalResults || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPromptStats = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        const [todayStats, monthlyStats] = await Promise.all([
          api.getPromptUsageStats({
            startDate: today.toISOString(),
          }),
          api.getPromptUsageStats({
            startDate: startOfMonth.toISOString(),
          }),
        ]);

        setPromptStats({
          todayTokens: todayStats.totalTokens || 0,
          todayCost: todayStats.totalCost || 0,
          monthlyCost: monthlyStats.totalCost || 0,
        });
      } catch (error) {
        console.error('Error fetching prompt stats:', error);
      } finally {
        setPromptLoading(false);
      }
    };

    fetchStats();
    fetchPromptStats();

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchPromptStats();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <MainLayout>
      <DashboardContainer theme={theme}>
        <Title theme={theme}>Dashboard</Title>
        <StatsGrid>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <AssignmentInd />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Total Customers</StatLabel>
              <StatValue theme={theme}>{loading ? '...' : stats.customers}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <People />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Total Users</StatLabel>
              <StatValue theme={theme}>{loading ? '...' : stats.users}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <Receipt />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Subscriptions</StatLabel>
              <StatValue theme={theme}>{loading ? '...' : stats.subscriptions}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <Notifications />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Notifications</StatLabel>
              <StatValue theme={theme}>{loading ? '...' : stats.notifications}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>

        <Title theme={theme} style={{ marginTop: '32px' }}>Prompt Usage</Title>
        <StatsGrid>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <Storage />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Today's Tokens</StatLabel>
              <StatValue theme={theme}>
                {promptLoading ? '...' : promptStats.todayTokens.toLocaleString()}
              </StatValue>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <AttachMoney />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Today's Cost</StatLabel>
              <StatValue theme={theme}>
                {promptLoading ? '...' : `$${promptStats.todayCost.toFixed(2)}`}
              </StatValue>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme}>
              <TrendingUp />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Monthly Cost</StatLabel>
              <StatValue theme={theme}>
                {promptLoading ? '...' : `$${promptStats.monthlyCost.toFixed(2)}`}
              </StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>
      </DashboardContainer>
    </MainLayout>
  );
};

export default Dashboard;

